import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Empty } from '../../../components/ui';
import UserService from '../../../services/user.service';
import { formatCurrency } from '../../../components/custom-hooks';
import { PencilIcon } from 'lucide-react';
import { Pencil, Trash2, ShoppingCart, Tag, Calendar, User, Clock, ArrowLeft } from 'lucide-react';
import { useConfirm } from '../../../components/useConfirm';
import { isAfricanCountry } from '../../../utils/constants';


// SVG Icons as React components
const ArrowLeftIcon = ({ className = "w-5 h-5" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
);

const TrashIcon = ({ className = "w-5 h-5" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
);

const CalendarIcon = ({ className = "w-4 h-4" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
);

const UserIcon = ({ className = "w-4 h-4" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
);

const TagIcon = ({ className = "w-4 h-4" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
    </svg>
);

interface CartItem {
    programId: number;
    cohortId: number;
    cohort: string;
    child: {
        id: number;
        firstName: string;
        lastName: string;
        age: number;
        gender: string;
    };
    package: {
        id: number;
        title: string;
        name: string;
        imageUrl: string;
        weekDuration: number;
        amount: number;
        altAmount: number;
    };
    Parent: {
        id: number;
        country: string;
    };
    nextClassDate: string;
    endClassDate: string;
}

export default function CartPage() {

    const userService = new UserService();
    const navigate = useNavigate();
    const { id } = useParams();

    const [loading, setLoading] = useState(true);
    const [cartItems, setCartItems] = useState<CartItem[]>([]);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteId, setDeleteId] = useState<number | null>(null);
    const [couponCode, setCouponCode] = useState('');
    const [userInfo, setUserInfo] = useState<any>([])
    const [subtotal, setSubtotal] = useState(0);
    const [discount, setDiscount] = useState(0);
    const { confirm, ConfirmDialog } = useConfirm();


    useEffect(() => {
        getCart();
    }, []);

    useEffect(() => {
        calculateTotals();
    }, [cartItems, userInfo]);

    const getCart = async () => {
        setLoading(true);
        try {
            const response = await userService.getCartDetails(Number(id));
            if (response.status) {
                const items = Array.isArray(response.data) ? response.data : [];
                setCartItems(items);
                setUserInfo(items[0]?.parent || null);
            }
        } catch (err: any) {
            toast.error(err?.message || 'Failed to load cart');
        } finally {
            setLoading(false);
        }
    };

    const calculateTotals = () => {
        let total = 0;
        cartItems.forEach(item => {
            const rate = Number(userInfo?.rate) || 1;
            const african = isAfricanCountry(userInfo?.country);
            if (african) {
                total += (item.package.altAmount || 0) * rate;
            } else {
                total += (item.package.amount || 0) * rate;
            }
        });
        setSubtotal(total);
    };

    const handleDeleteItem = async (programId: number) => {
        const ok = await confirm("Are you sure you want to remove this program from your cart?");
        if (!ok) return;

        setDeleteId(programId);
        setDeleteLoading(true);
        try {
            const response = await userService.deleteProgram(programId);
            if (response.status) {
                toast.success('Item removed from cart');
                getCart(); // Refresh cart
                setTimeout(() => {
                    window.location.reload()
                }, 2000);
            } else {
                toast.error(response.message);
            }
        } catch (err: any) {
            toast.error(err?.message || 'Failed to remove item');
        } finally {
            setDeleteLoading(false);
            setDeleteId(null);
        }

    };

    const handleCheckout = () => {
        if (couponCode.length > 0) {
            window.open(`https://api-pro.rydlearning.com/common/payment-init/${userInfo.id}/${couponCode}`, '_blank')
        } else {
            window.open(`https://api-pro.rydlearning.com/common/payment-init/${userInfo.id}`, '_blank')
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getPrice = (item: CartItem) => {
        const rate = Number(userInfo?.rate) || 1;
        const base = isAfricanCountry(userInfo?.country) ? item.package.altAmount : item.package.amount;
        return (base || 0) * rate;
    };

    const getCurrencyCode = () => {
        if (userInfo?.currencyCode) return userInfo.currencyCode;
        return isAfricanCountry(userInfo?.country) ? 'NGN' : 'CAD';
    };

    const totalAmount = subtotal - discount;

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="h-12 w-12 rounded-full border-2 border-ryd-primary border-l-transparent animate-spin"></div>
            </div>
        );
    }

    const handleClick = async () => {
        const ok = await confirm("Are you sure you want to change this program DAY/TIME?");
        if (!ok) return;

        localStorage.setItem("openModal", "true");
        window.open("/parent/sign-in", "_blank");

    };

    const handleSignIn = async () => {
        navigate("/parent/sign-in");
    };

    return (

        <div className="min-h-screen bg-gradient-to-br from-[#FBF8FC] via-[#F7F4F8] to-[#F3ECFF]">
            {/* Header Section */}
            <div className="bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                            <div className="bg-gradient-to-br from-[#7A2F78] via-ryd-primary to-[#E05AAE] p-3 rounded-2xl shadow-lg">
                                <ShoppingCart className="w-7 h-7 text-white" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">Cart</h1>
                                <p className="text-gray-600 text-sm mt-1">
                                    {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'} in your cart
                                </p>
                            </div>
                        </div>
                        {/* <button className="flex items-center gap-2 text-ryd-primary hover:text-ryd-primary font-medium transition-colors px-4 py-2 rounded-lg hover:bg-ryd-primaryLess1">
              <ArrowLeft className="w-4 h-4" />
              Continue Shopping
            </button> */}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {cartItems.length === 0 ? (
                    <div className="bg-white rounded-3xl shadow-lg p-12 text-center">
                        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <ShoppingCart className="w-12 h-12 text-gray-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
                        <p className="text-gray-600 mb-8">Add some programs to get started!</p>
                        <button onClick={handleSignIn} className="bg-gradient-to-r from-[#7A2F78] via-ryd-primary to-[#E05AAE] text-white px-8 py-3 rounded-xl hover:opacity-95 transition-all shadow-lg hover:shadow-xl">
                            Browse Programs
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        {/* Cart Items - Takes up 8 columns on large screens */}
                        <div className="lg:col-span-8 space-y-6">
                            {/* Edit Notice Banner */}
                            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
                                <div className="bg-amber-100 rounded-lg p-2 flex-shrink-0">
                                    <Pencil className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-amber-900 font-medium">Need to change class schedule?</p>
                                    <p className="text-sm text-amber-700 mt-1">Login to your profile to edit day and time of classes</p>
                                </div>
                            </div>

                            {/* Cart Items List */}
                            {cartItems.map((item) => (
                                <div key={item.programId} className="bg-white rounded-3xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100">
                                    <div className="p-6 sm:p-8">
                                        <div className="flex flex-col lg:flex-row gap-6">
                                            {/* Course Image */}
                                            <div className="flex-shrink-0">
                                                <div className="relative group">
                                                    <img
                                                        src={item.package.imageUrl}
                                                        alt={item.package.name}
                                                        className="w-full lg:w-32 h-48 lg:h-32 rounded-2xl object-cover shadow-md group-hover:scale-105 transition-transform duration-300"
                                                    />
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
                                                </div>
                                            </div>

                                            {/* Course Details */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex justify-between items-start gap-4 mb-4">
                                                    <div className="flex-1">
                                                        <h3 className="font-bold text-xl text-gray-900 mb-1 leading-tight">
                                                            {item.package.name}
                                                        </h3>
                                                        <p className="text-ryd-primary font-medium text-sm">
                                                            {item.package.title}
                                                        </p>
                                                    </div>
                                                    <div className="flex gap-2 flex-shrink-0">
                                                        <button
                                                            onClick={handleClick}
                                                            className="p-2.5 text-gray-400 hover:text-ryd-primary hover:bg-ryd-primaryLess1 rounded-xl transition-all"
                                                            title="Edit"
                                                        >
                                                            <Pencil className="w-5 h-5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteItem(item.programId)}
                                                            disabled={deleteLoading && deleteId === item.programId}
                                                            className="p-2.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all disabled:opacity-50"
                                                            title="Remove"
                                                        >
                                                            <Trash2 className={`w-5 h-5 ${deleteLoading && deleteId === item.programId ? 'animate-spin' : ''}`} />
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Student Info Card */}
                                                <div className="bg-gradient-to-r from-[#FBF8FC] to-[#F3ECFF] rounded-xl p-4 mb-4 border border-[#efe6f1]">
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <User className="w-4 h-4 text-ryd-primary" />
                                                        <span className="font-semibold text-gray-900">
                                                            {item.child.firstName} {item.child.lastName}
                                                        </span>
                                                        <span className="text-gray-600">•</span>
                                                        <span className="text-gray-700">{item.child.age} years</span>
                                                        <span className="text-gray-600">•</span>
                                                        <span className="text-gray-700">{item.child.gender}</span>
                                                    </div>
                                                </div>

                                                {/* Schedule Info */}
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                                                    <div className="flex items-center gap-2 text-sm bg-green-50 rounded-lg p-3 border border-green-100">
                                                        <Calendar className="w-4 h-4 text-green-600 flex-shrink-0" />
                                                        <div>
                                                            <p className="text-gray-600 text-xs">Starts</p>
                                                            <p className="font-semibold text-gray-900">{formatDate(item.nextClassDate)}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-sm bg-orange-50 rounded-lg p-3 border border-orange-100">
                                                        <Calendar className="w-4 h-4 text-orange-600 flex-shrink-0" />
                                                        <div>
                                                            <p className="text-gray-600 text-xs">Ends</p>
                                                            <p className="font-semibold text-gray-900">{formatDate(item.endClassDate)}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Badges and Price Row */}
                                                <div className="flex flex-wrap items-center justify-between gap-3">
                                                    <div className="flex flex-wrap gap-2">
                                                        <span className="inline-flex items-center gap-1.5 bg-ryd-primaryLess1 text-ryd-primary px-3 py-1.5 rounded-full text-xs font-semibold">
                                                            <Clock className="w-3.5 h-3.5" />
                                                            {item.cohort}
                                                        </span>
                                                        <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 px-3 py-1.5 rounded-full text-xs font-semibold">
                                                            {item.package.weekDuration} Weeks
                                                        </span>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-2xl font-bold bg-gradient-to-r from-[#7A2F78] via-ryd-primary to-[#E05AAE] bg-clip-text text-transparent">
                                                            {getCurrencyCode()} {formatCurrency(getPrice(item))}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Order Summary - Takes up 4 columns on large screens */}
                        <div className="lg:col-span-4">
                            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden sticky top-8">
                                {/* Summary Header */}
                                <div className="bg-gradient-to-r from-[#7A2F78] via-ryd-primary to-[#E05AAE] p-6">
                                    <h2 className="text-xl font-bold text-white">Order Summary</h2>
                                </div>

                                <div className="p-6 space-y-6">
                                    {/* Promo Code */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-900 mb-3">
                                            Have a promo code?
                                        </label>
                                        <div className="relative">
                                            <Tag className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                            <input
                                                type="text"
                                                value={couponCode}
                                                onChange={(e) => setCouponCode(e.target.value)}
                                                placeholder="Enter code"
                                                className="w-full border-2 border-gray-200 rounded-xl pl-11 pr-4 py-3.5 focus:outline-none focus:ring-2 focus:ring-ryd-primary focus:border-transparent transition-all text-sm"
                                            />
                                        </div>
                                    </div>

                                    {/* Divider */}
                                    <div className="border-t border-gray-200"></div>

                                    {/* Pricing Breakdown */}
                                    <div className="space-y-4">
                                        <div className="flex justify-between items-center text-gray-700">
                                            <span className="text-sm">Subtotal</span>
                                            <span className="font-semibold">{getCurrencyCode()} {formatCurrency(subtotal)}</span>
                                        </div>
                                        {discount > 0 && (
                                            <div className="flex justify-between items-center text-green-600">
                                                <span className="text-sm">Discount</span>
                                                <span className="font-semibold">-{getCurrencyCode()} {formatCurrency(discount)}</span>
                                            </div>
                                        )}

                                        {/* Total */}
                                        <div className="bg-gradient-to-r from-[#FBF8FC] to-[#F3ECFF] rounded-xl p-4 border-2 border-ryd-primary/20">
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-900 font-semibold">Total Amount</span>
                                                <span className="text-2xl font-bold bg-gradient-to-r from-[#7A2F78] via-ryd-primary to-[#E05AAE] bg-clip-text text-transparent">
                                                    {getCurrencyCode()} {formatCurrency(totalAmount)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Checkout Button */}
                                    <button
                                        onClick={handleCheckout}
                                        className="w-full bg-gradient-to-r from-[#7A2F78] via-ryd-primary to-[#E05AAE] text-white py-4 rounded-xl hover:opacity-95 transition-all font-semibold text-base shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                                    >
                                        Proceed to Checkout
                                    </button>

                                    {/* Security Notice */}
                                    <div className="text-center">
                                        <p className="text-xs text-gray-500 leading-relaxed">
                                            🔒 Secure checkout powered by industry-leading encryption
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Benefits Cards */}
                            <div className="mt-6 space-y-3">
                                {/* <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                  <div className="flex items-start gap-3">
                    <div className="bg-green-100 rounded-lg p-2">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Money-back guarantee</p>
                      <p className="text-xs text-gray-600 mt-1">Full refund within 7 days</p>
                    </div>
                  </div>
                </div> */}
                                <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                                    <div className="flex items-start gap-3">
                                        <div className="bg-ryd-primaryLess1 rounded-lg p-2">
                                            <svg className="w-5 h-5 text-ryd-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-gray-900 text-sm">Flexible scheduling</p>
                                            <p className="text-xs text-gray-600 mt-1">Change times anytime</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <ConfirmDialog />
        </div>
    );
}